// Server-derived coverage -> the view models the coverage surfaces render.
//
// bead hf-e6-commitments-coverage-udt.6.
//
// ── WHAT THIS MODULE IS NOT ALLOWED TO DO ──────────────────────────────────────
//
// It does not count anything. Not commitments, not people, not needs. Every number
// below arrives from `convex/coverage/read.ts`, which ran the kernel's engine
// (COV-001, COV-002, invariant 1). This module turns instants into local-time labels
// and server-supplied shapes into rendering models, and that is the whole of its job.
//
// The distinction matters more than it looks. `planData.ts` next door calls
// `deriveCoverage` in the BROWSER, because the Phase-1 surfaces run on fixtures with
// no server behind them. That is a demo affordance. On the live path there is exactly
// one derivation, it happens on the server, and a second one here would be the
// drifting answer COV-002 exists to forbid — the two would agree in every test and
// disagree the first time a commitment went stale in production.
//
// ── PLAN HEALTH IS READ, NOT ASSEMBLED ─────────────────────────────────────────
//
// `WeekCoverage.summary` is counted on the server. This module passes it through
// untouched rather than summing the days it happens to hold, because a tally the
// browser assembled goes wrong exactly when it matters: a partially loaded week
// reading healthier than it is.

import type { coverage as contracts } from "@herefor/contracts";
import { instant, timeZone, type TimeZoneId } from "@herefor/domain-kernel";
import type { UserVisibleState } from "@herefor/product-language";

import { formatWindowInZone, userVisibleStateKey } from "../plan/planData";

/**
 * One need's coverage, ready to render.
 *
 * `counts` is null for a single-person need: "1 of 1 person confirmed" beside a chip
 * that already says Covered is noise, and COV-006 says the count exists to express a
 * shortfall against a capacity greater than one.
 */
export interface NeedCoverageView {
  readonly supportNeedId: string;
  readonly label: string;
  /** Opaque identifiers only. Null when there is no plan ref to build a route from. */
  readonly href: string | null;
  readonly windowLabel: string;
  readonly startsAt: number;
  readonly stateKey: UserVisibleState;
  readonly counts: { readonly confirmed: number; readonly required: number } | null;
  /** A display treatment alongside the state, never a sixth state (plan §10.2). */
  readonly isPast: boolean;
  /**
   * People asked and not yet answered (REQ-003).
   *
   * Names, so the surface can say "Mary has been asked" rather than "somebody has".
   * It sits beside the state and never alters it: the need still reads Needs someone.
   */
  readonly awaitingReplyNames: readonly string[];
  /** People who agreed before a material change and have not reconfirmed (COV-004). */
  readonly awaitingRecheckNames: readonly string[];
}

export interface CoverageDayView {
  readonly localDate: string;
  /** "Tuesday 12 March", in the circle's zone. */
  readonly dayLabel: string;
  readonly needs: readonly NeedCoverageView[];
  readonly needingSomeone: number;
  readonly awaitingRecheck: number;
  readonly covered: number;
}

/** The week's roll-up, exactly as the server counted it. */
export type PlanHealthView = contracts.CoverageSummary;

export interface CoverageWeekView {
  readonly weekStartLocal: string;
  readonly weekStartLabel: string;
  readonly timeZone: TimeZoneId;
  readonly days: readonly CoverageDayView[];
  readonly health: PlanHealthView;
}

/** How a plan-health panel should open. A decision about emphasis, never the words. */
export type PlanHealthTone = "nothingPlanned" | "allHandled" | "outstanding";

export interface CoverageContext {
  /** Builds `/c/<ref>/needs/<id>`. Absent when the surface has no route to offer. */
  readonly publicPlanRef?: string;
}

/* ================================================================ week view === */

export function toCoverageWeekView(
  week: contracts.WeekCoverage,
  context: CoverageContext,
): CoverageWeekView {
  const zone = timeZone(week.timeZone);

  return {
    weekStartLocal: week.weekStartLocal,
    weekStartLabel: formatLocalDate(week.weekStartLocal),
    timeZone: zone,
    days: week.days.map((day) => ({
      localDate: day.localDate,
      dayLabel: formatLocalDate(day.localDate),
      needs: day.needs.map((need) => toNeedCoverageView(need, zone, context)),
      // Straight through from the server's own per-day counts.
      needingSomeone: day.needingSomeone,
      awaitingRecheck: day.awaitingRecheck,
      covered: day.covered,
    })),
    health: week.summary,
  };
}

export function toNeedCoverageView(
  need: contracts.NeedCoverage,
  zone: TimeZoneId,
  context: CoverageContext,
): NeedCoverageView {
  return {
    supportNeedId: need.supportNeedId,
    label: need.label,
    href:
      context.publicPlanRef === undefined
        ? null
        : `/c/${context.publicPlanRef}/needs/${need.supportNeedId}`,
    // The NEED's own window (NED-002, NED-010). A helper told the event's time turns
    // up an hour late, which is the failure that rule exists to prevent.
    windowLabel: formatWindowInZone(zone, instant(need.startsAt), instant(need.endsAt)),
    startsAt: need.startsAt,
    stateKey: userVisibleStateKey(need.status),
    counts:
      need.requiredCapacity > 1
        ? { confirmed: need.confirmedCapacity, required: need.requiredCapacity }
        : null,
    isPast: need.isPast,
    awaitingReplyNames: need.awaitingReply.map((person) => person.displayName),
    awaitingRecheckNames: need.awaitingRecheck.map((person) => person.displayName),
  };
}

/** Every need in the week, in day then start order. For an attention-style list. */
export function needsOfWeek(week: CoverageWeekView): readonly NeedCoverageView[] {
  return week.days.flatMap((day) => day.needs);
}

/* ============================================================== plan health === */

/**
 * How the glance should open.
 *
 * Three cases, and the middle one is the one worth naming: a week with nothing in it
 * is NOT the same as a week where everything is covered. Saying "everything is
 * covered" over an empty week claims a success nobody achieved, and a family who has
 * not filled the week in yet would be told they had finished.
 */
export function planHealthTone(health: PlanHealthView): PlanHealthTone {
  if (health.totalNeeds === 0) return "nothingPlanned";
  return health.needingSomeone === 0 && health.awaitingRecheck === 0
    ? "allHandled"
    : "outstanding";
}

/* ================================================================= plumbing === */

/**
 * "Tuesday 12 March" from a `YYYY-MM-DD` local date.
 *
 * The date is ALREADY in the circle's zone — the server bucketed it there (invariant
 * 9) — so this must not re-apply a zone or it would shift the day back off the day it
 * was placed on. It is formatted at midday UTC for exactly that reason: noon is the
 * one instant no offset in use anywhere can push across a date boundary.
 */
export function formatLocalDate(localDate: string): string {
  const at = new Date(`${localDate}T12:00:00Z`);
  if (Number.isNaN(at.getTime())) return localDate;

  return at.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}
